/** Parsed `ingestion_field_definition.field_config` for CATEGORIES / ORG_CATEGORIES fields. */
export interface CategoriesFieldConfig {
    /** When non-empty, the end-user picker is limited to these category IDs. */
    categoryIds: number[];
    singleSelect: boolean;
    /** When true, category picker renders inline (no expand button). */
    expandAutomatically: boolean;
    /** Custom expand-button label when expandAutomatically is false. */
    buttonLabel?: string;
}

const DEFAULT_CONFIG: CategoriesFieldConfig = {
    categoryIds: [],
    singleSelect: false,
    expandAutomatically: false,
};

function normalizeCategoryIds(raw: unknown): number[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const ids: number[] = [];
    for (const v of raw) {
        const n = typeof v === "number" ? v : parseInt(String(v), 10);
        if (Number.isFinite(n) && n > 0) {
            ids.push(n);
        }
    }
    return [...new Set(ids)];
}

function normalizeButtonLabel(raw: unknown): string | undefined {
    if (raw == null || typeof raw !== "string") {
        return undefined;
    }
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Parses JSON from `fieldDef.fieldConfig` for category field types.
 * Unrelated keys (e.g. object-occurrence flags) are ignored.
 */
export function parseCategoriesFieldConfig(fieldConfig: string | null | undefined): CategoriesFieldConfig {
    if (fieldConfig == null || String(fieldConfig).trim() === "") {
        return { ...DEFAULT_CONFIG };
    }
    try {
        const raw = JSON.parse(fieldConfig) as Record<string, unknown>;
        const parsed: CategoriesFieldConfig = {
            categoryIds: normalizeCategoryIds(raw.categoryIds),
            singleSelect: raw.singleSelect === true,
            expandAutomatically: raw.expandAutomatically === true,
        };
        const buttonLabel = normalizeButtonLabel(raw.buttonLabel);
        if (buttonLabel) {
            parsed.buttonLabel = buttonLabel;
        }
        return parsed;
    } catch {
        return { ...DEFAULT_CONFIG };
    }
}

/** Persists categoryIds, singleSelect, expandAutomatically, and optional buttonLabel in `field_config`. */
export function stringifyCategoriesFieldConfig(config: CategoriesFieldConfig): string {
    const payload: Record<string, unknown> = {
        categoryIds: [...new Set(config.categoryIds.filter((id) => Number.isFinite(id) && id > 0))].sort(
            (a, b) => a - b,
        ),
        singleSelect: config.singleSelect === true,
        expandAutomatically: config.expandAutomatically === true,
    };
    const buttonLabel = normalizeButtonLabel(config.buttonLabel);
    if (buttonLabel) {
        payload.buttonLabel = buttonLabel;
    }
    return JSON.stringify(payload);
}
